/**
 * Page: EditStockVideo
 * Admin-only page to edit an existing stock video. Fetches video data, allows
 * AI-assisted updates, submits changes, and handles loading/error states.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import StockVideoForm from '../../../components/admin/stockVideos/StockVideoForm';
import { StockVideoAIModal } from '../../../components/admin/stockVideos/StockVideoAIModal';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { getStockVideo, updateStockVideo } from '../../../api/media/stockVideosApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import Footer from '../../../components/shared/Footer';

const EditStockVideo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const [stockVideo, setStockVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  // Redirect if not admin
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [user, isAdmin, navigate]);

  // Fetch stock video data on mount
  useEffect(() => {
    if (id && isAdmin) {
      fetchStockVideo();
    }
  }, [id, isAdmin]);

  const fetchStockVideo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getStockVideo(id);
      
      if (response?.stockVideo) {
        setStockVideo(response.stockVideo);
      } else {
        throw new Error('Stock video not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load stock video');
      addToast('Failed to load stock video', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openAIModal = () => {
    setIsAIModalOpen(true);
  };

  const handleAIGenerateSuccess = (aiGeneratedData) => {
    // Update stock video data with AI generated content
    setStockVideo(prevData => ({
      ...prevData,
      ...aiGeneratedData
    }));
    
    addToast('Stock video data has been generated and populated in the form!', 'success');
  };

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setSubmitError('');
      
      const response = await updateStockVideo(id, formData);
      
      if (response?.message) {
        addToast('Stock video updated successfully!', 'success');
        
        // Navigate back to manage page
        navigate('/app/private/manageStockVideos');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      
      let errorMessage = 'Failed to update stock video. Please try again.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setSubmitError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading edit stock video..." />
      </div>
    );
  }

  // Redirect happens in useEffect if not admin
  if (!isAdmin) {
    return null;
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        {/* Back button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/app/private/admin/stock-videos')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Stock Videos
          </button>
        </div>

        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Error Loading Stock Video
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/app/private/manageStockVideos')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Manage
          </button>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/stock-videos')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Stock Videos
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Stock Video</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Update the details for "{stockVideo?.title}"
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/private/manageStockVideos')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
          >
            Manage Stock Videos
          </button>
          <button
            onClick={openAIModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Sparkles className="h-4 w-4" />
            Use AI
          </button>
        </div>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        {submitError && (
          <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Updating Stock Video</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <StockVideoForm
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          initialData={stockVideo}
          submitButtonText="Update Stock Video"
        />
      </div>
      
      {/* AI Modal */}
      <StockVideoAIModal 
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onGenerateSuccess={handleAIGenerateSuccess}
      />

      <Footer />
    </div>
  );
};

export default EditStockVideo; 